/**
 * brandIdentifier is a URL-safe slug of the site name, generated by the launch
 * pipeline. It is NOT a backend contract: email sender identity comes from the
 * tenant record (Tenant.emailConfig / Tenant.branding, resolved by tenantId).
 * Historic values (LifeEssentialsClub, TomMillerServices, ByronBayToken,
 * TheConnectionCode) remain valid slugs; nothing "must match exactly" any more.
 *
 * AGENTS: do not invent brand values here. Ask Tom for the tenant's brand pack
 * (clients/<slug>/brand/ in TMS HQ, designed in Claude Design) and run
 * `yarn rebrand <brand-spec.json>` — see CLAUDE.md § Branding.
 *
 * Branding sources, in precedence order (see REBRANDING.md):
 *   1. Tenant.branding in the backend — runtime SSOT, overlaid at boot
 *   2. this file — pre-fetch defaults, written by scripts/apply-brand-spec.js
 *      from the tenant's brand pack (clients/<slug>/brand/brand-spec.json in TMS HQ)
 */

export interface BrandConfig {
  // Core Brand Identity
  siteName: string;                // Main site/brand name displayed in headers and titles
  tagline: string;                 // Short brand slogan or value proposition
  description: string;             // Longer brand description for about sections and meta tags

  // Technical Configuration
  tenantId: string;                // Unique MongoDB tenant ID for data isolation
  apiUrl?: string;                 // Optional override for GraphQL API endpoint
  dograh?: {                       // Self-hosted Dograh voice-AI server (see managing-local-ai Runbook 38)
    apiBaseUrl: string;            // e.g. http://100.67.29.73:8000 (Tailscale-internal for now)
    embedToken: string;            // origin-locked PUBLIC embed token for the intake workflow (created on the server)
  };
  brandIdentifier: string;         // Backend brand identifier for email templates and notifications

  // Logo and Images
  logo: {
    glyph: string;                // Emoji glyph for text-only contexts (P2Onboard, chat header)
    src: string;                  // DEPRECATED alias of `glyph` — kept equal to it; do not put a URL here
    imageUrl?: string;            // Wide lockup image URL — overlaid from Tenant.branding.logo (default; emails/PDFs)
    imageUrlDark?: string;        // Lockup for dark backgrounds — Tenant.branding.logoOnDark (site dark mode; falls back to imageUrl)
    imageUrlLight?: string;       // Lockup for light backgrounds — Tenant.branding.logoOnLight (site light mode; falls back to imageUrl)
    squareUrl?: string;           // Square mark image URL — overlaid from Tenant.branding.logoSquare
    alt: string;                  // Alt text for accessibility
    favicon: string;              // Browser favicon path
  };
  colorMode?: "light" | "dark";   // Site default colour mode (viewer preference still wins unless locked)
  colorModeLocked?: boolean;      // true = the site has ONE palette: force colorMode, hide the toggle (light-only brands)
  hideSiteName?: boolean;         // Wordmark logos already carry the name: don't print siteName beside the logo
  hideTagline?: boolean;          // Suppress the tagline wherever the brand block prints it

  // Contact Information
  contact: {
    businessType: string;         // Type of business (e.g., "Business & Blockchain Solutions")
    industry: string;             // Industry category (e.g., "Technology")
    email?: string;               // Primary contact email
    phone?: string;               // Primary contact phone
    address?: string;             // Business physical address
    contactName?: string;         // Primary contact person name
    businessName?: string;        // Legal business name
  };

  // Color Palette
  colors: {
    // Additional accent colors (used throughout the app)
    accentBlue?: string;          // Secondary blue accent color
    primaryBlue?: string;         // Main blue for CTAs and highlights
    primaryBlueHover?: string;    // Hover state for primary blue elements
    starYellow?: string;          // Color for star/favorite icons
    starYellowHover?: string;     // Hover state for star icons
    successGreen?: string;        // Success indicators and checkmarks
    purpleAccent?: string;        // Purple accent for special highlights
    
    // Email module specific colors
    taskCardBg?: string;          // Background for task cards in task lists
    emailPreviewBg?: string;      // Background for email content preview areas
    darkCardBg?: string;          // Dark background for cards in dark theme
    darkBorderSubtle?: string;    // Subtle borders in dark theme
    
    // Highlight colors (for text highlighting and badges)
    highlightYellow?: string;     // Yellow highlight background
    highlightGreen?: string;      // Green highlight background
    highlightBlue?: string;       // Blue highlight background
    highlightPurple?: string;     // Purple highlight background
    highlightRed?: string;        // Red highlight background
    
    badges?: any;                 // Badge color schemes with bg, color, and border
    
    // Primary brand colors
    primary: string;              // Main brand color (buttons, links, primary actions)
    primaryHover: string;         // Hover state for primary color elements
    secondary: string;            // Secondary brand color (secondary actions)
    secondaryHover: string;       // Hover state for secondary color elements

    // Accent colors
    accent: string;               // Accent color for highlights and special elements
    accentLight: string;          // Lighter variant of accent color

    // Background colors
    background: {
      main: string;               // Main page background color
      light: string;              // Light background for sections
      card: string;               // Card/panel background color
      overlay: string;            // Modal/overlay background with transparency
      surface: string;            // Surface color for elevated elements
      darkSurface?: string;       // Dark theme surface color
      cardGradient?: string;      // Gradient background for cards in dark theme
      darkOverlay?: string;       // Dark overlay with transparency
      taskCard?: string;          // Task item background in task lists
      previewBg?: string;         // Email/content preview area background
    };

    // Status colors
    status: {
      success: string;            // Success states, confirmations, completed items
      warning: string;            // Warning states, caution indicators
      error: string;              // Error states, failed operations, deletions
      info: string;               // Informational states, hints, tips
      draft?: string;             // Draft email/document status
      sent?: string;              // Sent email status
      failed?: string;            // Failed email/operation status
      scheduled?: string;         // Scheduled email/task status
      inbound?: string;           // Inbound/received email status
    };

    // Text colors
    text: {
      primary: string;            // Primary text color (headings, important text)
      secondary: string;          // Secondary text color (body text)
      muted: string;              // Muted text (hints, placeholders, less important)
      inverse: string;            // Inverse text (white text on dark backgrounds)
      primaryDark?: string;       // Primary text for dark theme
      secondaryDark?: string;     // Secondary text for dark theme
      mutedDark?: string;         // Muted text for dark theme
    };

    // Border colors
    border: {
      light: string;              // Light borders (subtle separators)
      medium: string;             // Medium borders (standard borders)
      dark: string;               // Dark borders (emphasized borders)
      darkCard?: string;          // Card borders in dark theme
    };
  };

  // Typography
  fonts: {
    heading: string;              // Font family for headings (h1-h6)
    body: string;                 // Font family for body text and UI elements
  };

  // Component styling
  components: {
    button: {
      primaryBg: string;          // Primary button background color
      primaryHover: string;       // Primary button hover background
      secondaryBg: string;        // Secondary button background color
      secondaryHover: string;     // Secondary button hover background
      whiteBg?: string;           // White button background (for dark backgrounds)
      whiteText?: string;         // White button text color
      whiteHover?: string;        // White button hover background
      blueBg?: string;            // Blue button background (email module)
      blueHover?: string;         // Blue button hover background
    };
    tooltip?: {
      bg: string;                 // Tooltip background color
      color: string;              // Tooltip text color
      errorBg?: string;           // Error tooltip background
      darkBg?: string;            // Dark theme tooltip background
    };
    menu?: {
      bg: string;                 // Dropdown menu background
      itemBg: string;             // Menu item background
      itemColor: string;          // Menu item text color
      itemHoverBg: string;        // Menu item hover background
      itemHoverColor: string;     // Menu item hover text color
      borderColor: string;        // Menu border color
    };
    card: {
      bg: string;                 // Card background color
      border: string;             // Card border color
      shadow: string;             // Card shadow (elevation)
      darkShadow?: string;        // Card shadow in dark theme
      backdropFilter?: string;    // Backdrop filter for glass effect
      hoverShadow?: string;       // Card shadow on hover
    };
    navbar: {
      bg: string;                 // Navigation bar background
      text: string;               // Navigation bar text color
      shadow: string;             // Navigation bar shadow
    };
    footer: {
      bg: string;                 // Footer background color
      text: string;               // Footer text color
      linkColor: string;          // Footer link color
      linkHover: string;          // Footer link hover color
    };
    form: {
      bg: string;                 // Form background color
      fieldBg: string;            // Input field background
      fieldBorder: string;        // Input field border
      fieldBorderFocus: string;   // Input field border when focused
      fieldShadow: string;        // Input field shadow
      fieldShadowFocus: string;   // Input field shadow when focused
      labelColor: string;         // Form label text color
      placeholderColor: string;   // Input placeholder text color
    };
  };

  // Business specific
  membership: {
    totalLegacyMemberships: number;  // Total number of legacy members
    memberBenefits: {
      discount: string;               // Member discount description
      features: string[];             // List of member benefits/features
    };
  };
}

// Tenant Configuration - Update these values for your business
export const brandConfig: BrandConfig = {
  // Core Brand Identity
  siteName: "TalentLink+",  // @brand siteName
  tagline: "Qualify candidates before you open a single CV.",  // @brand tagline
  description: "Candidates complete role-specific forms, upload their CV and clear background checks in one public portal. You get a ready-scored shortlist — no wasted screening calls.",  // @brand description
  // Technical Configuration
  tenantId: "69e9b94c3612654bce60f4be",  // @brand tenantId
  brandIdentifier: "TalentLink",  // @brand brandIdentifier
  // Logo and Images
  // Lines carrying `// @brand <path>` are rewritten by scripts/apply-brand-spec.js
  // (local, from a brand-spec.json) and by the launch pipeline's brand_config
  // step (from Tenant.branding). Keep the marker on the same line as the value.
  logo: {
    glyph: "＋",                                   // @brand logo.glyph
    src: "🏢",                                     // deprecated alias — keep equal to glyph
    imageUrl: "/brand/logo-wide.png",                                  // @brand logo.imageUrl
    imageUrlDark: "/brand/logo-on-dark.svg",                              // @brand logo.imageUrlDark
    imageUrlLight: "/brand/logo-on-light.svg",                             // @brand logo.imageUrlLight
    squareUrl: "/brand/logo-square.svg",                                 // @brand logo.squareUrl
    alt: "TalentLink+ logo",
    favicon: "/favicon.svg"            // @brand logo.favicon
  },
  colorMode: "light",                               // @brand colorMode
  colorModeLocked: true,                           // @brand colorModeLocked
  hideSiteName: true,                             // @brand hideSiteName
  hideTagline: false,                              // @brand hideTagline

  // Contact Information
  contact: {
    businessType: "Recruitment & HR Technology Platform",
    industry: "Human Resources & Talent Acquisition",
    email: "support@talentlinkplus.com.au",  // @brand contact.email
    phone: "",  // @brand contact.phone
    address: "",  // @brand contact.address
    contactName: "Talent Link Plus",  // @brand contact.contactName
    businessName: "Talent Link Plus",  // @brand contact.businessName
  },

  // Color Palette - Mac-inspired Fresh White/Blue
  colors: {
    // Primary brand colors (Mac-like blue)
    primary: "#5980a6",  // @brand colors.primary
    primaryHover: "#416180",  // @brand colors.primaryHover
    secondary: "#1d2d3d",  // @brand colors.secondary
    secondaryHover: "#2c455d",  // @brand colors.secondaryHover

    // Accent colors
    accent: "#5980a6",  // @brand colors.accent
    accentLight: "#d6ebff",  // @brand colors.accentLight

    // Background colors (Dark gradient theme)
    background: {
      main: "#f2f2f3",  // @brand colors.backgroundMain
      light: "#FAFAFA",        // Off-white
      card: "#FFFFFF",         // White cards
      overlay: "rgba(0, 122, 255, 0.05)", // Light blue overlay
      surface: "#F5F5F7",      // Mac surface gray
      // New dark theme additions
      darkSurface: "#08090a",
      cardGradient: "linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.6) 50%, rgba(20, 20, 20, 0.8) 100%)",
      darkOverlay: "rgba(0, 0, 0, 0.2)",
      taskCard: "rgba(45, 45, 55, 0.3)",     // Dark gray for task items
      previewBg: "rgba(45, 45, 55, 0.3)"      // Email preview background
    },

    // Status colors (Mac system colors)
    status: {
      success: "#30D158",      // Mac green
      warning: "#FF9F0A",      // Mac orange
      error: "#FF3B30",        // Mac red
      info: "#5980a6",         // Mac blue
      draft: "#6B7280",        // Gray for draft status
      sent: "#22C55E",         // Green for sent
      failed: "#EF4444",       // Red for failed
      scheduled: "#3B82F6",    // Blue for scheduled
      inbound: "#8B5CF6"       // Purple for inbound
    },

    // Text colors (Mac-like typography)
    text: {
      primary: "#1D1D1F",      // Mac primary text
      secondary: "#6E6E73",    // Mac secondary text
      muted: "#8E8E93",        // Mac tertiary text
      inverse: "#FFFFFF",      // White text
      // Dark theme text colors
      primaryDark: "#FFFFFF",
      secondaryDark: "rgba(255, 255, 255, 0.7)",
      mutedDark: "rgba(255, 255, 255, 0.5)"
    },

    // Border colors (Subtle Mac borders)
    border: {
      light: "rgba(0, 0, 0, 0.05)",   // Very light border
      medium: "rgba(0, 0, 0, 0.1)",   // Light border
      dark: "rgba(0, 0, 0, 0.2)",     // Medium border
      // Dark theme borders
      darkCard: "rgba(255, 255, 255, 0.1)"
    },
    
    // Additional colors (shared across themes)
    accentBlue: "#369eff",
    primaryBlue: "#3B82F6",
    primaryBlueHover: "#2563EB",
    starYellow: "#FBBF24",
    starYellowHover: "#F59E0B",
    successGreen: "#22C55E",
    purpleAccent: "#A855F7",
    
    // Email module specific colors
    taskCardBg: "rgba(45, 45, 55, 0.3)",
    emailPreviewBg: "rgba(45, 45, 55, 0.3)",
    darkCardBg: "rgba(20, 20, 20, 0.9)",
    darkBorderSubtle: "rgba(255, 255, 255, 0.05)",
    highlightYellow: "#FEF3C7",
    highlightGreen: "#D1FAE5",
    highlightBlue: "#DBEAFE",
    highlightPurple: "#E9D5FF",
    highlightRed: "#FEE2E2",
    
    // Badge colors with transparency
    badges: {
      blue: {
        bg: "rgba(59, 130, 246, 0.2)",
        color: "#3B82F6",
        border: "rgba(59, 130, 246, 0.3)"
      },
      green: {
        bg: "rgba(34, 197, 94, 0.2)",
        color: "#22C55E",
        border: "rgba(34, 197, 94, 0.3)"
      },
      purple: {
        bg: "rgba(168, 85, 247, 0.2)",
        color: "#A855F7",
        border: "rgba(168, 85, 247, 0.3)"
      },
      yellow: {
        bg: "rgba(251, 191, 36, 0.2)",
        color: "#FBBF24",
        border: "rgba(251, 191, 36, 0.3)"
      },
      gray: {
        bg: "rgba(107, 114, 128, 0.2)",
        color: "#6B7280",
        border: "rgba(107, 114, 128, 0.3)"
      },
      orange: {
        bg: "rgba(251, 146, 60, 0.2)",
        color: "#FB923C",
        border: "rgba(251, 146, 60, 0.3)"
      },
      teal: {
        bg: "rgba(20, 184, 166, 0.2)",
        color: "#14B8A6",
        border: "rgba(20, 184, 166, 0.3)"
      },
      cyan: {
        bg: "rgba(6, 182, 212, 0.2)",
        color: "#06B6D4",
        border: "rgba(6, 182, 212, 0.3)"
      }
    }
  },

  // Typography (Thunder Compute uses Geist font)
  fonts: {
    heading: "Barlow Condensed",  // @brand fonts.heading
    body: "Barlow"  // @brand fonts.body
  },

  // Component styling
  components: {
    button: {
      primaryBg: "#5980a6",
      primaryHover: "#0051D0",
      secondaryBg: "rgba(0, 122, 255, 0.1)",
      secondaryHover: "rgba(0, 122, 255, 0.2)",
      // Dark theme button styles (actually for light buttons on dark backgrounds)
      whiteBg: "#FFFFFF",
      whiteText: "#000000",
      whiteHover: "#F5F5F5",
      // Email module specific buttons
      blueBg: "#3B82F6",
      blueHover: "#2563EB"
    },
    tooltip: {
      bg: "#3B82F6",
      color: "#FFFFFF",
      errorBg: "#EF4444",
      darkBg: "#1a1a2e"
    },
    menu: {
      bg: "rgba(60, 60, 60, 0.98)",           // Silver-ish dark background
      itemBg: "transparent",                   // Transparent item background
      itemColor: "#E5E5E5",                   // Light silver text
      itemHoverBg: "rgba(192, 192, 192, 0.2)", // Silver hover background
      itemHoverColor: "#FFFFFF",               // White text on hover
      borderColor: "rgba(192, 192, 192, 0.3)"  // Silver border
    },
    card: {
      bg: "#FFFFFF",
      border: "rgba(0, 0, 0, 0.06)",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
      // Dark theme card styles
      darkShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
      hoverShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
      backdropFilter: "blur(10px)"
    },
    navbar: {
      bg: "#08090a",
      text: "#FFFFFF",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
    },
    footer: {
      bg: "#141414",
      text: "rgba(255, 255, 255, 0.7)",
      linkColor: "#369eff",
      linkHover: "#1d2d3d"
    },
    form: {
      bg: "#FFFFFF",
      fieldBg: "#FFFFFF",
      fieldBorder: "rgba(0, 0, 0, 0.1)",
      fieldBorderFocus: "#5980a6",
      fieldShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
      fieldShadowFocus: "0 0 0 3px rgba(0, 122, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05)",
      labelColor: "#1D1D1F",
      placeholderColor: "#8E8E93"
    }
  },

  // Business specific
  membership: {
    totalLegacyMemberships: 500,
    memberBenefits: {
      discount: "Professional rates",
      features: [
        "CRM & Client Management",
        "Opportunities Pipeline",
        "Proposals & Digital Signing",
        "Calendar & Bookings",
        "Email Campaigns",
        "Learning Management",
        "Invoicing & Bills"
      ]
    }
  },

};

// Theme-specific color configurations
export const lightTheme = {
  // Primary brand colors
  primary: "#5980a6",  // @brand colors.primary
  primaryHover: "#416180",  // @brand colors.primaryHover
  secondary: "#1d2d3d",  // @brand colors.secondary
  secondaryHover: "#2c455d",  // @brand colors.secondaryHover
  accent: "#5980a6",  // @brand colors.accent
  accentLight: "#d6ebff",  // @brand colors.accentLight
  background: {
    main: "#f2f2f3",  // @brand colors.backgroundMain
    light: "#F7FAFC",
    card: "#FFFFFF",
    overlay: "rgba(0, 0, 0, 0.05)",
    surface: "#F5F5F7",
    cardGradient: "linear-gradient(135deg, #FFFFFF 0%, #F7FAFC 100%)",
    darkOverlay: "rgba(0, 0, 0, 0.1)",
    taskCard: "#F7FAFC",
    previewBg: "#F7FAFC"
  },

  // Text colors (Light theme)
  text: {
    primary: "#1A202C",
    secondary: "#4A5568",
    muted: "#718096",
    inverse: "#FFFFFF",
    primaryDark: "#1A202C",
    secondaryDark: "#4A5568",
    mutedDark: "#718096"
  },

  // Border colors (Light theme)
  border: {
    light: "#E2E8F0",
    medium: "#CBD5E0",
    dark: "#A0AEC0",
    darkCard: "#E2E8F0"
  },

  // Component colors (Light theme)
  components: {
    card: {
      bg: "#FFFFFF",
      border: "#E2E8F0",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
      darkShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
      hoverShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
      backdropFilter: "none"
    },
    navbar: {
      bg: "#FFFFFF",
      text: "#1A202C",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
    },
    footer: {
      bg: "#F7FAFC",
      text: "#4A5568",
      linkColor: "#5980a6",
      linkHover: "#0051D0"
    },
    button: {
      primaryBg: "#5980a6",
      primaryHover: "#0051D0",
      secondaryBg: "rgba(0, 122, 255, 0.1)",
      secondaryHover: "rgba(0, 122, 255, 0.2)",
      whiteBg: "#1A202C",
      whiteText: "#FFFFFF",
      whiteHover: "#2D3748",
      blueBg: "#3B82F6",
      blueHover: "#2563EB"
    }
  }
};

export const darkTheme = {
  // Primary brand colors (same as current)
  primary: "#5980a6",  // @brand colors.primary
  primaryHover: "#416180",  // @brand colors.primaryHover
  secondary: "#1d2d3d",  // @brand colors.secondary
  secondaryHover: "#2c455d",  // @brand colors.secondaryHover
  accent: "#5980a6",  // @brand colors.accent
  accentLight: "#d6ebff",  // @brand colors.accentLight
  background: {
    main: "#08090a",  // dark base — NOT marked: TalentLink+ is light-only; the pack's backgroundMain is the LIGHT ground (marker on lightTheme.background.main)
    light: "#FAFAFA",
    card: "#FFFFFF",
    overlay: "rgba(0, 122, 255, 0.05)",
    surface: "#F5F5F7",
    darkSurface: "#08090a",
    cardGradient: "linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.6) 50%, rgba(20, 20, 20, 0.8) 100%)",
    darkOverlay: "rgba(0, 0, 0, 0.2)",
    taskCard: "rgba(45, 45, 55, 0.3)",
    previewBg: "rgba(45, 45, 55, 0.3)"
  },

  // Text colors (Dark theme - current values)
  text: {
    primary: "#FFFFFF",
    secondary: "rgba(255, 255, 255, 0.7)",
    muted: "rgba(255, 255, 255, 0.5)",
    inverse: "#1A202C",
    primaryDark: "#FFFFFF",
    secondaryDark: "rgba(255, 255, 255, 0.7)",
    mutedDark: "rgba(255, 255, 255, 0.5)"
  },

  // Border colors (Dark theme - current values)
  border: {
    light: "rgba(0, 0, 0, 0.05)",
    medium: "rgba(0, 0, 0, 0.1)",
    dark: "rgba(0, 0, 0, 0.2)",
    darkCard: "rgba(255, 255, 255, 0.1)"
  },

  // Component colors (Dark theme)
  components: {
    card: {
      bg: "#FFFFFF",
      border: "rgba(0, 0, 0, 0.06)",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
      darkShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
      hoverShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
      backdropFilter: "blur(10px)"
    },
    navbar: {
      bg: "#08090a",
      text: "#FFFFFF",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
    },
    footer: {
      bg: "#141414",
      text: "rgba(255, 255, 255, 0.7)",
      linkColor: "#369eff",
      linkHover: "#1d2d3d"
    },
    button: {
      primaryBg: "#5980a6",
      primaryHover: "#0051D0",
      secondaryBg: "rgba(0, 122, 255, 0.1)",
      secondaryHover: "rgba(0, 122, 255, 0.2)",
      whiteBg: "#FFFFFF",
      whiteText: "#000000",
      whiteHover: "#F5F5F5",
      blueBg: "#3B82F6",
      blueHover: "#2563EB"
    },
    form: {
      bg: "rgba(20, 20, 20, 0.8)",
      fieldBg: "rgba(30, 30, 35, 0.8)",
      fieldBorder: "rgba(255, 255, 255, 0.1)",
      fieldBorderFocus: "#5980a6",
      fieldShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
      fieldShadowFocus: "0 0 0 3px rgba(0, 122, 255, 0.2), 0 1px 2px rgba(0, 0, 0, 0.3)",
      labelColor: "#FFFFFF"
    }
  }
};

// Helper function with color mode support
export const getColor = (path: string, colorMode?: 'light' | 'dark'): string => {
  // If no color mode specified, try to get it from localStorage or default to dark
  const mode = colorMode || (localStorage.getItem('chakra-ui-color-mode') as 'light' | 'dark') || 'dark';
  const theme = mode === 'light' ? lightTheme : darkTheme;

  const keys = path.split(".");

  // Special handling for component paths
  if (keys[0] === "components") {
    let value: any = theme;
    for (const key of keys) {
      value = value[key];
      if (value === undefined) {
        // Try to find in brandConfig.colors for backwards compatibility
        let fallback: any = brandConfig.colors;
        const colorKeys = keys.slice(1);
        for (const key of colorKeys) {
          fallback = fallback[key];
          if (fallback === undefined) break;
        }
        if (fallback !== undefined) return fallback;

        console.warn(`Color path "${path}" not found in theme config for mode: ${mode}`);
        return "#5980a6"; // Fallback to primary
      }
    }
    return value;
  }

  // Default handling for colors object
  let value: any = theme;
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      // Try to find in brandConfig.colors for backwards compatibility
      let fallback: any = brandConfig.colors;
      for (const key of keys) {
        fallback = fallback[key];
        if (fallback === undefined) break;
      }
      if (fallback !== undefined) return fallback;

      console.warn(`Color path "${path}" not found in theme config for mode: ${mode}`);
      return "#5980a6"; // Fallback to primary
    }
  }

  return value;
};

export const getBrandValue = (path: string): any => {
  const keys = path.split(".");
  let value: any = brandConfig;

  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Brand config path "${path}" not found`);
      return null;
    }
  }

  return value;
};

export const getComponent = (component: string, property: string, colorMode?: 'light' | 'dark'): string => {
  // If no color mode specified, try to get it from localStorage or default to dark
  const mode = colorMode || (localStorage.getItem('chakra-ui-color-mode') as 'light' | 'dark') || 'dark';
  const theme = mode === 'light' ? lightTheme : darkTheme;

  const componentConfig = (theme.components as any)?.[component];
  if (!componentConfig) {
    // Fall back to brandConfig for backwards compatibility
    const fallbackConfig = (brandConfig.components as any)[component];
    if (fallbackConfig) {
      const value = fallbackConfig[property];
      if (value) return value;
    }
    console.warn(`Component "${component}" not found in theme config for mode: ${mode}`);
    return "#5980a6";
  }

  const value = componentConfig[property];
  if (!value) {
    // Fall back to brandConfig for backwards compatibility
    const fallbackConfig = (brandConfig.components as any)[component];
    if (fallbackConfig) {
      const fallbackValue = fallbackConfig[property];
      if (fallbackValue) return fallbackValue;
    }
    console.warn(`Property "${property}" not found for component "${component}" in mode: ${mode}`);
    return "#5980a6";
  }

  return value;
};


// CSS Custom Properties Generator
export const generateCSSCustomProperties = (): string => {
  const { colors } = brandConfig;

  return `
    :root {
      --brand-primary: ${colors.primary};
      --brand-primary-hover: ${colors.primaryHover};
      --brand-secondary: ${colors.secondary};
      --brand-secondary-hover: ${colors.secondaryHover};
      --brand-accent: ${colors.accent};
      --brand-accent-light: ${colors.accentLight};
      
      --brand-bg-main: ${colors.background.main};
      --brand-bg-light: ${colors.background.light};
      --brand-bg-card: ${colors.background.card};
      --brand-bg-overlay: ${colors.background.overlay};
      --brand-bg-surface: ${colors.background.surface};
      
      --brand-success: ${colors.status.success};
      --brand-warning: ${colors.status.warning};
      --brand-error: ${colors.status.error};
      --brand-info: ${colors.status.info};
      
      --brand-text-primary: ${colors.text.primary};
      --brand-text-secondary: ${colors.text.secondary};
      --brand-text-muted: ${colors.text.muted};
      --brand-text-inverse: ${colors.text.inverse};
      
      --brand-border-light: ${colors.border.light};
      --brand-border-medium: ${colors.border.medium};
      --brand-border-dark: ${colors.border.dark};
      
      --brand-font-heading: ${brandConfig.fonts.heading};
      --brand-font-body: ${brandConfig.fonts.body};
    }
  `;
};

// ---- Runtime tenant branding overlay (2026-07-06, extended 2026-09-02) ----
// Tenant.branding (fetched from the backend by <TenantBrandingLoader/>) is the
// RUNTIME SOURCE OF TRUTH for brand values; the hardcoded values above are the
// pre-fetch defaults and the per-field fallback when the tenant leaves a field
// unset. The design-time source is the brand pack in TMS HQ
// (clients/<slug>/brand/brand-spec.json) — see REBRANDING.md.
//
// Scope of the overlay: identity (siteName / tagline / description), the image
// logos + glyph + favicon, the six brand colours (+ derived hovers/light tint),
// the page background base, fonts, the site colour-mode default, the hide
// flags, and contact email. It patches lightTheme/darkTheme too, because
// getColor() resolves against those FIRST — without that patch a tenant colour
// only ever reached the CSS custom properties, never the module pages.
//
// Still design-system constants (NOT tenant data): the rest of the palettes
// (text, borders, surfaces), status/badge colours, and every components.* style.
export interface TenantBrandingOverlay {
  siteName?: string | null;
  tagline?: string | null;
  description?: string | null;
  favicon?: string | null;
  logo?: string | null;
  logoOnDark?: string | null;
  logoOnLight?: string | null;
  logoSquare?: string | null;
  logoGlyph?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  primaryColorHover?: string | null;
  secondaryColorHover?: string | null;
  backgroundMain?: string | null;
  colorMode?: "DARK" | "LIGHT" | string | null;
  colorModeLocked?: boolean | null;
  fontHeading?: string | null;
  fontBody?: string | null;
  hideSiteName?: boolean | null;
  hideTagline?: boolean | null;
}

// --- colour helpers (pure; exported for the tenant-management preview) ---
const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
const parseHex = (hex: string): [number, number, number] | null => {
  const m = hex.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
const toHex = ([r, g, b]: [number, number, number]) =>
  "#" + [r, g, b].map((v) => clamp(v).toString(16).padStart(2, "0")).join("").toUpperCase();
/** Mix a colour toward white (amount > 0) or black (amount < 0); amount in [-1, 1]. */
export const shadeColor = (hex: string, amount: number): string => {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const target = amount >= 0 ? 255 : 0;
  const a = Math.abs(amount);
  return toHex([rgb[0] + (target - rgb[0]) * a, rgb[1] + (target - rgb[1]) * a, rgb[2] + (target - rgb[2]) * a] as [number, number, number]);
};
export const relativeLuminance = (hex: string): number => {
  const rgb = parseHex(hex);
  if (!rgb) return 0;
  const lin = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
};
/** Hover shade: dark brands lighten on hover, light brands darken. */
export const deriveHoverColor = (hex: string): string =>
  relativeLuminance(hex) < 0.18 ? shadeColor(hex, 0.14) : shadeColor(hex, -0.16);
export const deriveLightTint = (hex: string): string => shadeColor(hex, 0.65);

/** Wrap a bare family name in the platform fallback stack; pass stacks through. */
const fontStack = (family: string, kind: "heading" | "body"): string => {
  if (family.includes(",")) return family;
  const quoted = /\s/.test(family) ? `'${family}'` : family;
  return kind === "heading"
    ? `${quoted}, -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif`
    : `${quoted}, -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif`;
};

export const applyTenantBranding = (
  branding: TenantBrandingOverlay | null | undefined,
  fromEmail?: string | null
): void => {
  if (!branding) return;
  const previousSiteName = brandConfig.siteName;

  // Identity
  if (branding.siteName) brandConfig.siteName = branding.siteName;
  if (branding.tagline) brandConfig.tagline = branding.tagline;
  if (branding.description) brandConfig.description = branding.description;
  if (typeof branding.hideSiteName === "boolean") brandConfig.hideSiteName = branding.hideSiteName;
  if (typeof branding.hideTagline === "boolean") brandConfig.hideTagline = branding.hideTagline;
  if (fromEmail) brandConfig.contact.email = fromEmail;

  // Logos
  if (branding.logoGlyph) {
    brandConfig.logo.glyph = branding.logoGlyph;
    brandConfig.logo.src = branding.logoGlyph; // deprecated alias stays in step
  }
  if (branding.logo) brandConfig.logo.imageUrl = branding.logo;
  if (branding.logoOnDark) brandConfig.logo.imageUrlDark = branding.logoOnDark;
  if (branding.logoOnLight) brandConfig.logo.imageUrlLight = branding.logoOnLight;
  if (branding.logoSquare) brandConfig.logo.squareUrl = branding.logoSquare;
  if (branding.favicon) brandConfig.logo.favicon = branding.favicon;

  // Colour mode + fonts
  if (branding.colorMode) {
    const m = String(branding.colorMode).toLowerCase();
    if (m === "light" || m === "dark") brandConfig.colorMode = m;
  }
  if (typeof branding.colorModeLocked === "boolean") brandConfig.colorModeLocked = branding.colorModeLocked;
  if (branding.fontHeading) brandConfig.fonts.heading = fontStack(branding.fontHeading, "heading");
  if (branding.fontBody) brandConfig.fonts.body = fontStack(branding.fontBody, "body");

  // Colours — applied to brandConfig.colors AND both theme palettes.
  // Guard: `primary` is the ACTION colour (buttons, links). A tenant record
  // whose primary equals its page background (TMS once stored #08090a for
  // both) would make every primary button vanish, so a primary with < 1.6:1
  // contrast against the current mode's background is ignored with a warning
  // — fix the record (put the dark base in backgroundMain, the action colour
  // in primary) rather than widening this guard.
  const modeForGuard = brandConfig.colorMode ?? "dark";
  const bgForGuard = branding.backgroundMain || (modeForGuard === "light" ? lightTheme : darkTheme).background.main;
  const contrastRatio = (a: string, b: string) => {
    const la = relativeLuminance(a), lb = relativeLuminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  };
  let primaryCandidate = branding.primaryColor || undefined;
  if (primaryCandidate && bgForGuard && contrastRatio(primaryCandidate, bgForGuard) < 1.6) {
    console.warn(`[brand] Tenant primaryColor ${primaryCandidate} is indistinguishable from the page background ${bgForGuard}; ignoring it. Set backgroundMain for the dark base and use an action colour for primary.`);
    primaryCandidate = undefined;
  }
  const primary = primaryCandidate;
  const secondary = branding.secondaryColor || undefined;
  const accent = branding.accentColor || undefined;
  const primaryHover = branding.primaryColorHover || (primary ? deriveHoverColor(primary) : undefined);
  const secondaryHover = branding.secondaryColorHover || (secondary ? deriveHoverColor(secondary) : undefined);
  const accentLight = accent ? deriveLightTint(accent) : undefined;
  const patch = (target: { primary: string; primaryHover: string; secondary: string; secondaryHover: string; accent: string; accentLight: string }) => {
    if (primary) target.primary = primary;
    if (primaryHover) target.primaryHover = primaryHover;
    if (secondary) target.secondary = secondary;
    if (secondaryHover) target.secondaryHover = secondaryHover;
    if (accent) target.accent = accent;
    if (accentLight) target.accentLight = accentLight;
  };
  patch(brandConfig.colors);
  patch(lightTheme);
  patch(darkTheme);
  if (branding.backgroundMain) {
    const mode = brandConfig.colorMode ?? "dark";
    (mode === "light" ? lightTheme : darkTheme).background.main = branding.backgroundMain;
    brandConfig.colors.background.main = branding.backgroundMain;
    if (mode === "dark") darkTheme.background.darkSurface = branding.backgroundMain;
  }

  // Re-apply the bootstrap side effects from index.tsx that captured the
  // hardcoded values before the fetch resolved. The title is only replaced
  // when it still holds the pre-fetch default — a page that already set its
  // own title via useDocumentTitle keeps it (2026-09-02).
  if (!document.title || document.title === previousSiteName) document.title = brandConfig.siteName;
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute("content", brandConfig.description);
  }
  if (branding.favicon) {
    const faviconEl = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    if (faviconEl) faviconEl.href = branding.favicon;
  }
  let overrideStyle = document.getElementById("tenant-brand-overrides") as HTMLStyleElement | null;
  if (!overrideStyle) {
    overrideStyle = document.createElement("style");
    overrideStyle.id = "tenant-brand-overrides";
    document.head.appendChild(overrideStyle);
  }
  overrideStyle.textContent = generateCSSCustomProperties();
};

export default brandConfig; 